import { InspectorIcon } from "../InspectorIcon";
import { isContextMenuKey } from "../../contextMenuKey";
import { orgUrlSegment } from "../../orgRouting";
import { Basket } from "@lucasmarkes/hairline/react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useOrgs } from "../../state/orgs";
import {
  useInboxTriage,
  matchesInboxTriage,
  type InboxTriage,
  type InboxTriageView,
  type InboxImportance,
} from "./inboxTriage";
import { InboxTriageMenuItems } from "./InboxTriageControls";
import type { InboxAgentNotification } from "@amoreg/contracts";
import { inboxItemTitle, notificationSourceLabel } from "./inboxPresentation";
import { NotificationBody, useNotificationLinkedRecords } from "./NotificationBody";
import { NotificationDestinationLink, NotificationRunLink } from "./NotificationRunLink";
import { InboxDetailFooter } from "./InboxDetailFooter";
import {
  notificationPrimaryDestination,
  notificationSecondaryDestinations,
} from "./inboxDetailActions";
/**
 * InboxPage — the standalone personal inbox at `/inbox`.
 *
 * Responsive master-detail: a resizable list pane on the left
 * (icon, title, one-line context, relative time), the selected item's full
 * story in the reading pane, and structured facts in a properties column.
 * Desktop previews the first item; mobile opens details on selection.
 *
 * Personal read receipts persist per signed-in account in this browser.
 * Items retire when their underlying fact changes (an accepted invitation
 * leaves, an assessed policy review leaves); reading doesn't resolve them.
 *
 * Item rendering is per-kind on the shared discriminated union; a new source
 * adds a row renderer, a detail body, and its properties — the frame stays.
 * Dev builds append sample items so the page can be designed
 * at volume before the real sources produce it.
 *
 * @module components/inbox/InboxPage
 */
import { ContextMenu as ContextMenuPrimitive } from "@base-ui/react/context-menu";
import {
  ArrowLeftIcon,
  MoreHorizontalIcon,
  SearchIcon,
  XIcon,
  ArrowUpRightIcon,
  SlidersHorizontalIcon,
  CopyIcon,
  CheckIcon,
  MailIcon,
  MailOpenIcon,
  PanelRightIcon,
  InboxIcon,
  RefreshCwIcon,
  StarIcon,
  GlobeIcon,
  FileTextIcon,
  Clock3Icon,
  Building2Icon,
  SparklesIcon,
  UserRoundIcon,
  UserPlusIcon,
} from "lucide-react";
import { useCallback, useContext, useEffect, useRef, useState } from "react";

import { cn, isMacPlatform } from "../../lib/utils";
import { shouldFocusSidebarSearch } from "../sidebar/SidebarSearch";
import { Kbd } from "../ui/kbd";
import { formatRelativeTime } from "../../timestampFormat";
import { createPortal } from "react-dom";
import { SidebarContentSlot } from "../sidebar/SidebarContentSlot";
import {
  useInbox,
  type InboxApprovalItem,
  type InboxInvitationItem,
  type InboxItem,
  type InboxPolicyReviewItem,
} from "../../state/inbox";
import { useAccountProfile } from "../../state/accountProfile";
import { useWorkflowRun } from "../../state/workflows";
import { ApprovalDecision } from "../workflows/ApprovalDecision";
import { ChecklistDone } from "../workflows/ChecklistDone";
import { Empty, EmptyHeader, EmptyMedia, EmptyTitle, EmptyDescription } from "../ui/empty";
import { HairlineArt } from "../ui/hairline-art";
import { Skeleton } from "../ui/skeleton";
import {
  Menu,
  MenuTrigger,
  MenuPopup,
  MenuGroup,
  MenuGroupLabel,
  MenuRadioGroup,
  MenuRadioItem,
  MenuCheckboxItem,
  MenuSeparator,
  MenuItem,
  MenuShortcut,
} from "../ui/menu";
import {
  filterInboxItems,
  inboxFilterCount,
  inboxItemText,
  relatedInboxItems,
  INBOX_SEVERITY as SEVERITY_BADGE,
  inboxItemKey as itemKey,
  inboxItemOrganizationName,
  inboxSpansOrganizations,
  scopeInboxItems,
  selectedInboxItem,
  type InboxFilter,
  type InboxSeverity,
  type InboxOrder,
} from "./inboxPresentation";
import { InputGroup, InputGroupAddon, InputGroupInput } from "../ui/input-group";
import { InboxContextPanel } from "./InboxContextPanel";
import {
  InboxActorAvatar,
  InboxStatusGlyph,
  inboxRowStatus,
  inboxRowSubtitle,
} from "./InboxRowParts";
import { InboxPolicyLinks } from "./InboxPolicyLinks";
import {
  newlyOpenedInboxItem,
  useInboxReadState,
  type InboxReadPresentation,
} from "./inboxReadState";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { useInboxScope } from "./useInboxScope";
import { useSidebar } from "../ui/sidebar";
import { toastManager } from "../ui/toast";
import "./inbox.css";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { isSampleInboxItem } from "./inboxSampleItems";

const ignoreSidebarChange = (_open: boolean) => {};

export function InboxPage({ organizationId = null }: { organizationId?: string | null }) {
  const { open, setOpen } = useSidebar();
  const inbox = useInbox();
  const readScope = useInboxScope();
  // One store, two views: `/inbox` spans every tenant the person belongs to,
  // `/$org/inbox` narrows the tenant-bound items to that org.
  const items = scopeInboxItems(inbox.items, organizationId);
  return (
    <InboxContent
      {...inbox}
      items={items}
      readScope={readScope}
      sidebarOpen={open}
      onSidebarOpenChange={setOpen}
    />
  );
}

export function InboxContent({
  items,
  available,
  isPending,
  failed,
  refresh,
  readScope = null,
  sidebarOpen = true,
  onSidebarOpenChange = ignoreSidebarChange,
}: {
  readonly items: readonly InboxItem[];
  readonly available: boolean;
  readonly isPending: boolean;
  readonly failed: boolean;
  readonly refresh: () => void;
  readonly readScope?: string | null;
  readonly sidebarOpen?: boolean;
  readonly onSidebarOpenChange?: (open: boolean) => void;
}) {
  const sidebarSlot = useContext(SidebarContentSlot);
  const { readKeys, setRead, canSetRead } = useInboxReadState(readScope);
  const triage = useInboxTriage(readScope);
  const [triageView, setTriageView] = useState<InboxTriageView>("inbox");
  const [importance, setImportance] = useState<InboxImportance>("all");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const changeTriage = (item: InboxItem, patch: InboxTriage) => {
    const key = itemKey(item);
    const previous = triage.states[key] ?? {};
    // Rows leave the list at once, no collapse: the list re-settles in one
    // frame, the way Linear's does.
    const persisted = triage.change(key, patch);
    if (!persisted)
      toastManager.add({
        type: "warning",
        title: "Saved for this session",
        description: "Browser storage is unavailable, so this may reset on reload.",
      });
    if (patch.settled || patch.snoozedUntil) {
      const toastId = toastManager.add({
        type: "success",
        title: patch.settled ? "Update archived" : "Update snoozed",
        actionProps: {
          children: "Undo",
          onClick: () => {
            triage.change(key, { settled: false, snoozedUntil: undefined, ...previous });
            toastManager.close(toastId);
          },
        },
      });
    }
  };
  const markRead = (item: InboxItem, read: boolean) => {
    if (!setRead(itemKey(item), read))
      toastManager.add({
        type: "warning",
        title: "Read status saved for this session",
        description: "Browser storage is unavailable, so it may reset on reload.",
      });
  };
  // `?item=` opens straight onto one item (the bell in the top strip links this way).
  const requestedKey = useSearch({
    strict: false,
    select: (search) => (typeof (search as { item?: unknown }).item === "string" ? ((search as { item: string }).item) : null),
  });
  const [selectedKey, setSelectedKey] = useState<string | null>(requestedKey);
  const [selectionClosed, setSelectionClosed] = useState(false);
  const [jurisdiction, setJurisdiction] = useState("");
  const [filter, setFilter] = useState<InboxFilter>("all");
  const [query, setQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [severity, setSeverity] = useState<InboxSeverity>("all");
  const [order, setOrder] = useState<InboxOrder>("newest");
  const [showProperties, setShowProperties] = useState(true);
  const desktop = useMediaQuery("md");
  const showList = useCallback(() => onSidebarOpenChange(true), [onSidebarOpenChange]);
  const searchRef = useRef<HTMLInputElement>(null);
  const searchButtonRef = useRef<HTMLButtonElement>(null);
  const searchShortcut =
    typeof navigator !== "undefined" && isMacPlatform(navigator.platform) ? "⌘ K" : "Ctrl K";
  const openSearch = useCallback(() => {
    setSearchOpen(true);
    showList();
    requestAnimationFrame(() => {
      searchRef.current?.focus();
      searchRef.current?.select();
    });
  }, [showList]);
  useEffect(() => {
    // "/" searches this section, as it does in governance and settings;
    // Mod+K stays the strip's search-everything.
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.isComposing || !shouldFocusSidebarSearch(event)) return;
      // Leave shortcuts inside another open dialog to its owner.
      if (document.querySelector('[role="dialog"][data-open]')) return;
      event.preventDefault();
      event.stopPropagation();
      openSearch();
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [openSearch]);
  const listRef = useRef<HTMLUListElement>(null);
  const detailRef = useRef<HTMLElement>(null);
  const visibleItems = items.filter((item) =>
    matchesInboxTriage(triage.states[itemKey(item)] ?? {}, triageView, importance, triage.now),
  );
  const filteredItems = filterInboxItems(visibleItems, filter, {
    query,
    severity,
    order,
    jurisdiction,
  }).filter((item) => !unreadOnly || !readKeys.has(itemKey(item)));
  // Tag rows with their tenant only where the list actually mixes tenants —
  // on the org-scoped view every row shares one org, so the tag is noise.
  // Read off the source set, not the filtered one: the tag must not appear and
  // vanish as the reader narrows a search. Hidden samples do not count.
  const spansOrganizations = inboxSpansOrganizations(items);
  const activeFilterCount =
    inboxFilterCount(filter, severity, jurisdiction) +
    Number(triageView !== "inbox") +
    Number(importance !== "all");
  const hasFilters = activeFilterCount > 0 || query.trim().length > 0;
  const jurisdictions = [
    ...new Set(
      items.flatMap((item) => (item.kind === "policy-review" ? [item.jurisdiction] : [])),
    ),
  ].sort();
  const selected = selectionClosed ? null : selectedInboxItem(filteredItems, selectedKey);
  const showDetail = !sidebarOpen && selected !== null;
  const lastOpened = useRef<InboxReadPresentation>(null);
  // Track the visible presentation, not readKeys: an explicit unread action must stick.
  useEffect(() => {
    const presentation =
      selected && readScope && (desktop || showDetail)
        ? { scope: readScope, key: itemKey(selected) }
        : null;
    const openedKey = newlyOpenedInboxItem(lastOpened.current, presentation);
    lastOpened.current = presentation;
    if (openedKey && selected && !readKeys.has(openedKey)) markRead(selected, true);
  });
  const openItem = (item: InboxItem) => {
    // Also handles reopening the already selected item after marking it unread.
    if (canSetRead && !readKeys.has(itemKey(item))) markRead(item, true);
    setSelectionClosed(false);
    setSelectedKey(itemKey(item));
    if (!desktop) onSidebarOpenChange(false);
    requestAnimationFrame(() => {
      listRef.current
        ?.querySelector<HTMLButtonElement>('[data-inbox-row][aria-pressed="true"]')
        ?.scrollIntoView({ block: "nearest" });
      if (!desktop) detailRef.current?.focus();
    });
  };
  const selectedIndex = selected ? filteredItems.indexOf(selected) : -1;
  const selectAt = (index: number, focusRow = false) => {
    const item = filteredItems[index];
    if (!item) return;
    setSelectionClosed(false);
    setSelectedKey(itemKey(item));
    requestAnimationFrame(() => {
      const row = listRef.current?.querySelectorAll<HTMLButtonElement>("[data-inbox-row]")[index];
      row?.scrollIntoView({ block: "nearest" });
      if (focusRow) row?.focus();
    });
  };
  const contextPanel = selected ? (
    <InboxContextPanel
      item={selected}
      related={relatedInboxItems(
        items.filter((item) =>
          matchesInboxTriage(triage.states[itemKey(item)] ?? {}, "inbox", "all", triage.now),
        ),
        selected,
      )}
      onFilter={(next) => {
        setFilter(next.kind ?? (next.jurisdiction ? "policy-review" : "all"));
        setJurisdiction(next.jurisdiction ?? "");
        setSeverity(next.severity ?? "all");
        setQuery(next.query ?? "");
        if (next.query) openSearch();
        showList();
        setSelectionClosed(false);
      }}
      onOpen={(item) => {
        if (!filteredItems.some((candidate) => itemKey(candidate) === itemKey(item))) {
          setTriageView("inbox");
          setImportance("all");
          setFilter("all");
          setSeverity("all");
          setJurisdiction("");
          setQuery("");
        }
        openItem(item);
      }}
    />
  ) : null;
  const filters = [
    { value: "all", label: "All updates" },
    { value: "policy-review", label: "Policy reviews" },
    { value: "agent-notification", label: "Agent updates" },
    { value: "invitation", label: "Invitations" },
  ] as const;

  const sidebarContent = (
    <aside
      id="inbox-sidebar"
      hidden={!desktop && !sidebarOpen}
      aria-hidden={!sidebarOpen}
      inert={!sidebarOpen}
      aria-label="Inbox items"
      className={cn(
        // Linear's list pane: 400 wide, same surface as the page, one hairline.
        // The rail's sidebar toggle slides it shut on desktop, the detail
        // taking the whole card; the content keeps its width so it slides
        // rather than squashes.
        "relative flex h-full min-h-0 shrink-0 flex-col overflow-hidden border-r transition-[width,border-color] duration-(--sidebar-motion-duration) ease-(--sidebar-motion-ease) md:[&>*]:min-w-[400px] motion-reduce:transition-none",
        sidebarSlot && desktop
          ? "w-full flex-1 border-border/60"
          : sidebarOpen
            ? "w-full border-border/60 md:w-[400px]"
            : "w-0 border-transparent",
        !desktop && !sidebarOpen && "hidden",
      )}
    >
      <header className="inbox-header flex h-10 shrink-0 items-center gap-2 px-4">
        <h1 className="text-[13px] font-medium text-foreground">
          {triageView === "settled" ? "Archive" : triageView === "snoozed" ? "Snoozed" : "Inbox"}
        </h1>
        <Menu>
          <MenuTrigger
            render={<Button aria-label="Inbox options" size="icon-sm" variant="ghost-muted" />}
          >
            <MoreHorizontalIcon />
          </MenuTrigger>
          <MenuPopup align="start" className="w-56">
            <MenuGroup>
              <MenuGroupLabel>View</MenuGroupLabel>
              <MenuRadioGroup
                value={triageView}
                onValueChange={(value) => {
                  if (value === "inbox" || value === "settled" || value === "snoozed")
                    setTriageView(value);
                }}
              >
                {(["inbox", "snoozed", "settled"] as const).map((view) => (
                  <MenuRadioItem key={view} value={view}>
                    <span className="flex w-full items-center justify-between gap-6">
                      <span className="capitalize">{view === "settled" ? "Archive" : view}</span>
                      <span className="text-xs tabular-nums text-muted-foreground">
                        {
                          items.filter((item) =>
                            matchesInboxTriage(
                              triage.states[itemKey(item)] ?? {},
                              view,
                              "all",
                              triage.now,
                            ),
                          ).length
                        }
                      </span>
                    </span>
                  </MenuRadioItem>
                ))}
              </MenuRadioGroup>
            </MenuGroup>
            <MenuSeparator />
            <MenuGroup>
              <MenuItem onClick={() => openSearch()}>
                <SearchIcon />
                Search updates
                <MenuShortcut>{searchShortcut}</MenuShortcut>
              </MenuItem>
              <MenuItem disabled={isPending} onClick={refresh}>
                <RefreshCwIcon className={cn(isPending && "motion-safe:animate-spin")} />
                Refresh
              </MenuItem>
            </MenuGroup>
            <MenuSeparator />
            <MenuGroup>
              <MenuGroupLabel>Sort updates</MenuGroupLabel>
              <MenuRadioGroup
                value={order}
                onValueChange={(value) => {
                  if (value === "newest" || value === "oldest" || value === "priority")
                    setOrder(value);
                }}
              >
                <MenuRadioItem value="newest">Newest first</MenuRadioItem>
                <MenuRadioItem value="oldest">Oldest first</MenuRadioItem>
                <MenuRadioItem value="priority">Priority first</MenuRadioItem>
              </MenuRadioGroup>
            </MenuGroup>
            <MenuSeparator />
            <MenuGroup>
              <MenuGroupLabel>Display</MenuGroupLabel>
              <MenuCheckboxItem checked={showProperties} onCheckedChange={setShowProperties}>
                Show properties
              </MenuCheckboxItem>
            </MenuGroup>
          </MenuPopup>
        </Menu>
        <div className="ml-auto flex items-center gap-1">
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  ref={searchButtonRef}
                  aria-label="Search updates"
                  aria-pressed={searchOpen}
                  size="icon-sm"
                  variant={searchOpen ? "secondary" : "ghost-muted"}
                  onClick={() => (searchOpen ? (setQuery(""), setSearchOpen(false)) : openSearch())}
                >
                  <SearchIcon />
                </Button>
              }
            />
            <TooltipPopup side="bottom">
              Search updates <Kbd className="ml-1">/</Kbd>
            </TooltipPopup>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  aria-label={unreadOnly ? "Show all updates" : "Show unreads only"}
                  aria-pressed={unreadOnly}
                  size="icon-sm"
                  variant={unreadOnly ? "secondary" : "ghost-muted"}
                  onClick={() => setUnreadOnly((value) => !value)}
                >
                  {/* The icon nudges on each change; while the filter is on a
                      small primary dot sits on its top-right corner. */}
                  <span key={String(unreadOnly)} className="inbox-toggle-pop relative flex">
                    <MailOpenIcon />
                    <span
                      aria-hidden
                      className={cn(
                        "absolute -top-1 -right-1 size-1.5 rounded-full bg-primary ring-2 ring-background transition-transform duration-150 ease-out motion-reduce:transition-none",
                        unreadOnly ? "scale-100" : "scale-0",
                      )}
                    />
                  </span>
                </Button>
              }
            />
            <TooltipPopup side="bottom">{unreadOnly ? "Show all updates" : "Show unreads only"}</TooltipPopup>
          </Tooltip>
          <Menu>
            <MenuTrigger
              render={
                <Button
                  aria-label={activeFilterCount > 0 ? `Filter inbox, ${activeFilterCount} active` : "Filter inbox"}
                  title="Filter"
                  size="icon-sm"
                  className="relative"
                  variant={activeFilterCount === 0 ? "ghost-muted" : "secondary"}
                />
              }
            >
              <SlidersHorizontalIcon />
              {activeFilterCount > 0 && (
                <span aria-hidden className="absolute -top-0.5 -right-0.5 size-1.5 rounded-full bg-primary" />
              )}
            </MenuTrigger>
            <MenuPopup align="end" className="w-52">
              <MenuGroup>
                <MenuGroupLabel>Importance</MenuGroupLabel>
                <MenuRadioGroup
                  value={importance}
                  onValueChange={(value) => {
                    if (value === "all" || value === "important" || value === "ordinary")
                      setImportance(value);
                  }}
                >
                  <MenuRadioItem value="all">Any importance</MenuRadioItem>
                  <MenuRadioItem value="important">Important</MenuRadioItem>
                  <MenuRadioItem value="ordinary">Not important</MenuRadioItem>
                </MenuRadioGroup>
              </MenuGroup>
              <MenuSeparator />
              <MenuGroup>
                <MenuGroupLabel>Show updates</MenuGroupLabel>
                <MenuRadioGroup
                  value={filter}
                  onValueChange={(value) => {
                    if (
                      value === "all" ||
                      value === "policy-review" ||
                      value === "invitation" ||
                      value === "agent-notification"
                    ) {
                      setFilter(value);
                      showList();
                    }
                  }}
                >
                  {filters.map(({ value, label }) => (
                    <MenuRadioItem key={value} value={value}>
                      <span className="flex items-center justify-between gap-6">
                        {label}
                        <span className="text-xs tabular-nums text-muted-foreground">
                          {filterInboxItems(visibleItems, value).length}
                        </span>
                      </span>
                    </MenuRadioItem>
                  ))}
                </MenuRadioGroup>
              </MenuGroup>
              <MenuSeparator />
              <MenuGroup>
                <MenuGroupLabel>Review priority</MenuGroupLabel>
                <MenuRadioGroup
                  value={severity}
                  onValueChange={(value) => {
                    if (
                      value === "all" ||
                      value === "high" ||
                      value === "medium" ||
                      value === "low"
                    ) {
                      setSeverity(value);
                      showList();
                    }
                  }}
                >
                  <MenuRadioItem value="all">Any priority</MenuRadioItem>
                  <MenuRadioItem value="high">Action needed</MenuRadioItem>
                  <MenuRadioItem value="medium">Review</MenuRadioItem>
                  <MenuRadioItem value="low">Informational</MenuRadioItem>
                </MenuRadioGroup>
              </MenuGroup>
              {jurisdictions.length > 0 && (
                <>
                  <MenuSeparator />
                  <MenuGroup>
                    <MenuGroupLabel>Jurisdiction</MenuGroupLabel>
                    <MenuRadioGroup
                      value={jurisdiction}
                      onValueChange={(value) => {
                        setJurisdiction(value);
                        showList();
                      }}
                    >
                      <MenuRadioItem value="">All jurisdictions</MenuRadioItem>
                      {jurisdictions.map((name) => (
                        <MenuRadioItem key={name} value={name}>
                          {name}
                        </MenuRadioItem>
                      ))}
                    </MenuRadioGroup>
                  </MenuGroup>
                </>
              )}
            </MenuPopup>
          </Menu>
        </div>
      </header>
      {searchOpen && (
        <div className="inbox-header px-3 py-2">
          <InputGroup variant="ghost" className="rounded-md">
            <InputGroupAddon>
              <SearchIcon className="size-3.5 text-muted-foreground" />
            </InputGroupAddon>
            <InputGroupInput
              ref={searchRef}
              autoFocus
              onKeyDown={(event) => {
                if (event.key === "Escape") {
                  event.preventDefault();
                  event.stopPropagation();
                  setQuery("");
                  setSearchOpen(false);
                  searchButtonRef.current?.focus();
                }
                if (event.key === "ArrowDown" && filteredItems.length > 0) {
                  event.preventDefault();
                  selectAt(0, true);
                }
              }}
              size="sm"
              type="search"
              aria-label="Search updates"
              placeholder="Search titles, policies, jurisdictions…"
              value={query}
              onValueChange={(value) => {
                setQuery(value);
                showList();
              }}
            />
            {query && (
              <InputGroupAddon align="inline-end">
                <Button
                  aria-label="Clear search"
                  size="icon-micro"
                  variant="ghost-muted"
                  onClick={() => setQuery("")}
                >
                  <XIcon />
                </Button>
              </InputGroupAddon>
            )}
          </InputGroup>
        </div>
      )}
      {hasFilters && (
        <div className="inbox-header flex flex-wrap items-center gap-1.5 px-3 pb-2 pt-1">
          {[
            ...(triageView !== "inbox"
              ? [
                  {
                    label: triageView === "settled" ? "Archived" : "Snoozed",
                    clear: () => setTriageView("inbox"),
                  },
                ]
              : []),
            ...(importance !== "all"
              ? [
                  {
                    label: importance === "important" ? "Important" : "Not important",
                    clear: () => setImportance("all"),
                  },
                ]
              : []),
            ...(filter !== "all"
              ? [
                  {
                    label: filters.find((entry) => entry.value === filter)!.label,
                    clear: () => setFilter("all"),
                  },
                ]
              : []),
            ...(severity !== "all"
              ? [{ label: SEVERITY_BADGE[severity].label, clear: () => setSeverity("all") }]
              : []),
            ...(jurisdiction ? [{ label: jurisdiction, clear: () => setJurisdiction("") }] : []),
            ...(query.trim() ? [{ label: `“${query.trim()}”`, clear: () => setQuery("") }] : []),
          ].map(({ label, clear }) => (
            <Button
              key={label}
              size="micro"
              variant="outline"
              className="max-w-full gap-1 rounded-md text-[10px]"
              aria-label={`Remove filter: ${label}`}
              onClick={clear}
            >
              <span className="truncate">{label}</span>
              <XIcon className="size-2.5" />
            </Button>
          ))}
          <Button
            size="micro"
            variant="ghost-muted"
            className="ml-auto text-[10px]"
            onClick={() => {
              setTriageView("inbox");
              setImportance("all");
              setFilter("all");
              setSeverity("all");
              setJurisdiction("");
              setQuery("");
            }}
          >
            Clear all
          </Button>
        </div>
      )}
      {failed && items.length > 0 && (
        <div
          role="status"
          className="border-b border-border px-4 py-3 text-xs text-muted-foreground"
        >
          Couldn't refresh. Showing the last available updates.
          <Button size="micro" variant="ghost" onClick={refresh}>
            Retry
          </Button>
        </div>
      )}
      {filteredItems.length === 0 ? (
        <ListEmptyState
          available={available}
          failed={failed}
          isPending={isPending}
          onRetry={refresh}
          filtered={hasFilters}
        />
      ) : (
        <ul
          ref={listRef}
          aria-label="Updates"
          className="min-h-0 flex-1 scroll-pt-3 space-y-0.5 overflow-y-auto px-1 pb-1 pt-3"
          onKeyDown={(event) => {
            if (event.altKey || event.ctrlKey || event.metaKey) return;
            const buttons = Array.from(
              listRef.current?.querySelectorAll<HTMLButtonElement>("[data-inbox-row]") ?? [],
            );
            if (!(event.target as HTMLElement).matches("[data-inbox-row]")) return;
            const index = buttons.indexOf(
              (event.target as HTMLElement).closest("button") as HTMLButtonElement,
            );
            if (index < 0) return;
            const current = filteredItems[index];
            if (current && event.key.toLowerCase() === "u" && !event.shiftKey) {
              event.preventDefault();
              markRead(current, !readKeys.has(itemKey(current)));
              return;
            }
            if (current && event.key.toLowerCase() === "e" && !event.shiftKey) {
              event.preventDefault();
              const state = triage.states[itemKey(current)] ?? {};
              changeTriage(current, { settled: !state.settled, snoozedUntil: undefined });
              return;
            }
            const next =
              event.key === "ArrowDown"
                ? Math.min(index + 1, filteredItems.length - 1)
                : event.key === "ArrowUp"
                  ? Math.max(index - 1, 0)
                  : event.key === "Home"
                    ? 0
                    : event.key === "End"
                      ? filteredItems.length - 1
                      : null;
            if (next !== null) {
              event.preventDefault();
              selectAt(next, true);
            }
          }}
        >
          {filteredItems.map((item) => (
            <li key={itemKey(item)} data-inbox-item-key={itemKey(item)}>
              <InboxListRow
                item={item}
                showOrganization={spansOrganizations}
                triageState={triage.states[itemKey(item)] ?? {}}
                onTriageChange={(patch) => changeTriage(item, patch)}
                unread={!readKeys.has(itemKey(item))}
                canSetRead={canSetRead}
                onSetRead={(read) => markRead(item, read)}
                selected={selected !== null && itemKey(selected) === itemKey(item)}
                onSelect={() => openItem(item)}
              />
            </li>
          ))}
        </ul>
      )}
    </aside>
  );

  const unreadCount = filteredItems.filter((item) => !readKeys.has(itemKey(item))).length;
  const selectedTriage = selected ? (triage.states[itemKey(selected)] ?? {}) : {};

  return (
    <div
      data-inbox-page=""
      className="flex min-h-0 min-w-0 flex-1 overflow-hidden bg-background text-foreground"
    >
      {desktop && sidebarSlot ? createPortal(sidebarContent, sidebarSlot) : sidebarContent}
      <main
        ref={detailRef}
        tabIndex={-1}
        aria-label="Update details"
        className={cn(
          "flex min-h-0 min-w-0 flex-1 flex-col outline-none",
          sidebarOpen && "max-md:hidden",
        )}
      >
        {selected ? (
          <>
            <div className="inbox-header flex h-10 shrink-0 items-center px-2 md:hidden">
              <Button
                size="xs"
                variant="ghost"
                onClick={() => {
                  showList();
                  requestAnimationFrame(() =>
                    listRef.current
                      ?.querySelector<HTMLButtonElement>('[data-inbox-row][aria-pressed="true"]')
                      ?.focus(),
                  );
                }}
              >
                <ArrowLeftIcon />
                Back to inbox
              </Button>
            </div>
            <InboxItemDetail
              key={itemKey(selected)}
              item={selected}
              showProperties={showProperties}
              contextPanel={contextPanel}
              detailActions={{
                title: (
                  <>
                    <Tooltip>
                      <TooltipTrigger
                        render={
                          <Button
                            aria-label={selectedTriage.important ? "Remove from favorites" : "Add to favorites"}
                            aria-pressed={!!selectedTriage.important}
                            size="icon-sm"
                            variant="ghost-muted"
                            disabled={!triage.available}
                            onClick={() => changeTriage(selected, { important: !selectedTriage.important })}
                          >
                            <StarIcon
                              className={cn("size-3.5", selectedTriage.important && "fill-amber-400 text-amber-500")}
                            />
                          </Button>
                        }
                      />
                      <TooltipPopup>{selectedTriage.important ? "Remove from favorites" : "Favorite"}</TooltipPopup>
                    </Tooltip>
                    <Menu>
                      <MenuTrigger
                        render={<Button aria-label="More actions" size="icon-sm" variant="ghost-muted" />}
                      >
                        <MoreHorizontalIcon />
                      </MenuTrigger>
                      <MenuPopup align="start" className="w-56">
                        <MenuItem disabled={!canSetRead} onClick={() => markRead(selected, !readKeys.has(itemKey(selected)))}>
                          {readKeys.has(itemKey(selected)) ? <MailIcon /> : <CheckIcon />}
                          {readKeys.has(itemKey(selected)) ? "Mark as unread" : "Mark as read"}
                          <MenuShortcut>U</MenuShortcut>
                        </MenuItem>
                        <InboxTriageMenuItems
                          state={selectedTriage}
                          disabled={!triage.available}
                          onChange={(patch) => changeTriage(selected, patch)}
                        />
                        <MenuSeparator />
                        <MenuItem
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(inboxItemText(selected));
                              toastManager.add({ type: "success", title: "Update copied" });
                            } catch {
                              toastManager.add({ type: "error", title: "Couldn’t copy update" });
                            }
                          }}
                        >
                          <CopyIcon />
                          Copy update
                        </MenuItem>
                      </MenuPopup>
                    </Menu>
                  </>
                ),
                view: (
                  <>
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        <Button
                          aria-label={readKeys.has(itemKey(selected)) ? "Mark as unread" : "Mark as read"}
                          size="icon-sm"
                          variant="ghost-muted"
                          disabled={!canSetRead}
                          onClick={() => markRead(selected, !readKeys.has(itemKey(selected)))}
                        >
                          {readKeys.has(itemKey(selected)) ? <MailIcon /> : <CheckIcon />}
                        </Button>
                      }
                    />
                    <TooltipPopup>
                      {readKeys.has(itemKey(selected)) ? "Mark as unread" : "Mark as read"}
                    </TooltipPopup>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        <Button
                          aria-label="Copy update"
                          size="icon-sm"
                          variant="ghost-muted"
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(inboxItemText(selected));
                              toastManager.add({ type: "success", title: "Update copied" });
                            } catch {
                              toastManager.add({
                                type: "error",
                                title: "Couldn’t copy update",
                                description: "Select and copy the text directly.",
                              });
                            }
                          }}
                        >
                          <CopyIcon />
                        </Button>
                      }
                    />
                    <TooltipPopup>Copy update</TooltipPopup>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        <Button
                          aria-label="Toggle properties"
                          aria-pressed={showProperties}
                          size="icon-sm"
                          variant={showProperties ? "secondary" : "ghost-muted"}
                          onClick={() => setShowProperties(!showProperties)}
                        >
                          <PanelRightIcon />
                        </Button>
                      }
                    />
                    <TooltipPopup>
                      {showProperties ? "Hide properties" : "Show properties"}
                    </TooltipPopup>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        <Button
                          aria-label="Close update"
                          size="icon-sm"
                          variant="ghost-muted"
                          onClick={() => {
                            setSelectionClosed(true);
                            showList();
                            requestAnimationFrame(() =>
                              listRef.current?.querySelector<HTMLButtonElement>("button")?.focus(),
                            );
                          }}
                        >
                          <XIcon />
                        </Button>
                      }
                    />
                    <TooltipPopup>Close update</TooltipPopup>
                  </Tooltip>
                </>
                ),
              }}
            />
          </>
        ) : (
          <Empty>
            <EmptyHeader>
              <HairlineArt figure={Basket} className="mb-4 w-48 sm:w-56" />
              <EmptyTitle className="text-[13px] font-medium text-foreground/80">
                {unreadCount > 0
                  ? `${unreadCount} unread ${unreadCount === 1 ? "update" : "updates"}`
                  : "No update selected"}
              </EmptyTitle>
            </EmptyHeader>
          </Empty>
        )}
      </main>
    </div>
  );
}

function ListEmptyState({
  available,
  failed,
  isPending,
  onRetry,
  filtered = false,
}: {
  readonly available: boolean;
  readonly failed: boolean;
  readonly isPending: boolean;
  readonly onRetry: () => void;
  readonly filtered?: boolean;
}) {
  if (isPending)
    return (
      <div role="status" aria-label="Loading inbox" className="w-full max-w-md space-y-6 p-5">
        {[0, 1, 2].map((row) => (
          <div key={row} className="flex gap-3">
            <Skeleton className="size-8 shrink-0 rounded-lg motion-reduce:animate-none" />
            <div className="flex-1 space-y-2">
              <Skeleton className="motion-reduce:animate-none h-3 w-2/3" />
              <Skeleton className="motion-reduce:animate-none h-3 w-full" />
              <Skeleton className="motion-reduce:animate-none h-3 w-1/2" />
            </div>
          </div>
        ))}
      </div>
    );
  return (
    <Empty>
      <EmptyHeader>
        {available && !failed && !filtered ? null : (
          <EmptyMedia variant="icon">
            <InboxIcon />
          </EmptyMedia>
        )}
        <EmptyTitle className="text-[13px] font-medium text-foreground/80">
          {failed
            ? "Couldn't load your inbox"
            : !available
              ? "Inbox unavailable"
              : filtered
                ? "No matching updates"
                : "You're all caught up"}
        </EmptyTitle>
        <EmptyDescription className="max-w-64 text-xs leading-5">
          {failed
            ? "Try again to get your latest updates."
            : !available
              ? "The inbox isn't set up on this server."
              : filtered
                ? "Try another search or clear your filters."
                : "New policy reviews and invitations will appear here when they need your attention."}
        </EmptyDescription>
      </EmptyHeader>
      {failed && (
        <Button onClick={onRetry} size="sm" variant="outline">
          Try again
        </Button>
      )}
    </Empty>
  );
}

function relativeLabel(isoDate: string): string {
  const relative = formatRelativeTime(isoDate);
  return relative?.value ?? "";
}

// ---------------------------------------------------------------------------
// Per-kind renderers. A new inbox source adds its three pieces here.
// ---------------------------------------------------------------------------

function InboxListRow({
  item,
  selected,
  onSelect,
  unread,
  canSetRead,
  onSetRead,
  triageState,
  onTriageChange,
  showOrganization = false,
}: {
  readonly item: InboxItem;
  /** Prefix the context line with the item's tenant (cross-tenant lists). */
  readonly showOrganization?: boolean;
  readonly selected: boolean;
  readonly onSelect: () => void;
  readonly unread: boolean;
  readonly canSetRead: boolean;
  readonly onSetRead: (read: boolean) => void;
  readonly triageState: InboxTriage;
  readonly onTriageChange: (patch: InboxTriage) => void;
}) {
  const rowRef = useRef<HTMLButtonElement>(null);
  const title = inboxItemTitle(item);
  const summary = inboxRowSubtitle(item);
  // An invitation already names its organization in the title; tagging it
  // again would read as a duplicate.
  const organizationTag = showOrganization ? inboxItemOrganizationName(item) : null;
  return (
    <div className="relative" data-selected={selected || undefined}>
      <ContextMenuPrimitive.Root>
        <ContextMenuPrimitive.Trigger
          render={
            <Button
              data-inbox-row=""
              ref={rowRef}
              variant="ghost"
              aria-pressed={selected}
              onClick={onSelect}
              onKeyDown={(event) => {
                if (isContextMenuKey(event)) {
                  event.preventDefault();
                  event.stopPropagation();
                  const rect = event.currentTarget.getBoundingClientRect();
                  event.currentTarget.dispatchEvent(
                    new MouseEvent("contextmenu", {
                      bubbles: true,
                      cancelable: true,
                      clientX: rect.left + 24,
                      clientY: rect.top + rect.height / 2,
                    }),
                  );
                }
              }}
              className={cn(
                "group/inbox-row h-auto! w-full justify-start gap-3 whitespace-normal rounded-md border-0 px-3 py-2.5 text-left font-normal",
                // The kit's ordinary hover tint before a click; selection holds
                // a slightly stronger one.
                selected && "bg-foreground/7!",
              )}
            />
          }
        >
          <InboxActorAvatar item={item} className="mt-0.5" />
          <span className="min-w-0 flex-1">
            <span data-inbox-row-heading="" className="flex h-[18px] items-center gap-2">
              <span className="flex min-w-0 flex-1 items-center">
                <span
                  data-inbox-unread-space=""
                  className={cn(
                    "flex shrink-0 items-center overflow-hidden",
                    unread ? "mr-2 w-1.5" : "mr-0 w-0",
                  )}
                >
                  <span
                    role="img"
                    aria-label="Unread"
                    aria-hidden={!unread}
                    data-inbox-unread={unread ? "" : undefined}
                    className="size-1.5 shrink-0 rounded-full bg-primary"
                  />
                </span>
                {organizationTag !== null && (
                  <span
                    data-inbox-organization={organizationTag}
                    className="mr-1.5 shrink-0 text-[13px] font-medium text-muted-foreground"
                  >
                    {organizationTag}
                  </span>
                )}
                <span
                  data-inbox-title=""
                  className={cn(
                    "truncate text-[13px] leading-[18px] font-medium",
                    unread || selected ? "text-foreground" : "text-muted-foreground",
                  )}
                >
                  {title}
                </span>
              </span>
              {triageState.important && (
                <StarIcon
                  aria-label="Important"
                  className="size-3 shrink-0 fill-amber-500 text-amber-600"
                />
              )}
              <InboxStatusGlyph status={inboxRowStatus(item, triageState)} />
            </span>
            <span className="flex h-[18px] items-center gap-2 text-xs leading-[18px] font-normal text-muted-foreground">
              <span className="truncate">{summary}</span>
              <time dateTime={item.createdAt} className="ml-auto shrink-0 tabular-nums">
                {relativeLabel(item.createdAt)}
              </time>
            </span>
          </span>
        </ContextMenuPrimitive.Trigger>
        <MenuPopup
          side="right"
          align="start"
          sideOffset={2}
          finalFocus={rowRef}
          className="w-52"
          aria-label={`Actions for ${title}`}
        >
          <MenuItem disabled={!canSetRead} onClick={() => onSetRead(unread)}>
            {unread ? <CheckIcon /> : <MailIcon />}
            {unread ? "Mark as read" : "Mark as unread"}
            <MenuShortcut>U</MenuShortcut>
          </MenuItem>
          <InboxTriageMenuItems
            state={triageState}
            disabled={!canSetRead}
            onChange={onTriageChange}
          />
          <MenuSeparator />
          <MenuItem onClick={onSelect}>
            <ArrowUpRightIcon />
            Open update
          </MenuItem>
        </MenuPopup>
      </ContextMenuPrimitive.Root>
    </div>
  );
}

function InboxItemDetail({
  item,
  showProperties,
  contextPanel,
  detailActions,
}: {
  readonly item: InboxItem;
  readonly showProperties: boolean;
  readonly contextPanel: React.ReactNode;
  readonly detailActions: DetailActions;
}) {
  switch (item.kind) {
    case "agent-notification":
      return (
        <AgentNotificationDetail
          item={item}
          showProperties={showProperties}
          contextPanel={contextPanel}
          detailActions={detailActions}
        />
      );
    case "invitation":
      return (
        <InvitationDetail
          item={item}
          showProperties={showProperties}
          contextPanel={contextPanel}
          detailActions={detailActions}
        />
      );
    case "policy-review":
      return (
        <PolicyReviewDetail
          item={item}
          showProperties={showProperties}
          contextPanel={contextPanel}
          detailActions={detailActions}
        />
      );
    case "workflow-approval":
      return (
        <WorkflowApprovalDetail
          item={item}
          showProperties={showProperties}
          contextPanel={contextPanel}
          detailActions={detailActions}
        />
      );
  }
}

/**
 * A workflow run waiting on the reader's approval: what to check and the
 * decision, from the run itself, so it is current however old the inbox is.
 */
function WorkflowApprovalDetail({
  item,
  showProperties,
  contextPanel,
  detailActions,
}: {
  readonly item: InboxApprovalItem;
  readonly showProperties: boolean;
  readonly contextPanel: React.ReactNode;
  readonly detailActions: DetailActions;
}) {
  const { account } = useAccountProfile();
  const { read } = useWorkflowRun(item.organizationId, item.runId);
  const run = read?.kind === "ready" ? read.value : null;
  const step = run?.steps.find((entry) => entry.nodeId === item.nodeId) ?? null;
  // Decided, or moved on since the inbox was read: the item goes on the next refresh.
  const settled = run !== null && (step?.status !== "waiting" || step.attempt !== item.attempt);
  return (
    <DetailShell
      showProperties={showProperties}
      contextPanel={contextPanel}
      detailActions={detailActions}
      markerClassName="bg-destructive-foreground"
      kindLabel="Approval"
      title={`${item.workflowName}: ${item.stepLabel}`}
      properties={
        <>
          <PropertyRow label="Workflow" icon={<SparklesIcon />}>{item.workflowName}</PropertyRow>
          <PropertyRow label="Step" icon={<CheckIcon />}>{item.stepLabel}</PropertyRow>
          {item.startedByEmail ? (
            <PropertyRow label="Started by" icon={<UserRoundIcon />}>{item.startedByEmail}</PropertyRow>
          ) : null}
          <PropertyRow label="Organization" icon={<Building2Icon />}>{item.organizationName}</PropertyRow>
          <PropertyRow label="Waiting" icon={<Clock3Icon />}>{relativeLabel(item.waitingSince)}</PropertyRow>
        </>
      }
    >
      <h2 className="text-2xl font-semibold leading-[1.3] tracking-tight text-balance">
        {item.workflowName}: {item.stepLabel}
      </h2>
      <p className="mt-3 text-[15px] leading-7 text-foreground/90">
        {item.stepKind === "checklist" ? (
          "A run is waiting for you to do this step. Do it, then mark it done to let the run carry on."
        ) : (
          <>
            {item.startedByEmail ? (
              <>
                <strong className="font-medium text-foreground">{item.startedByEmail}</strong>'s run is waiting for your approval.
              </>
            ) : (
              "A run is waiting for your approval."
            )}{" "}
            Approve it to carry on, send it back to an earlier step with feedback, or reject it.
          </>
        )}
      </p>
      <div className="mt-6" data-inbox-approval={item.runId}>
        {run === null ? (
          read?.kind === "missing" ? (
            <p className="text-sm text-muted-foreground">This run no longer exists.</p>
          ) : read?.kind === "unavailable" ? (
            <p className="text-sm text-destructive">The run could not be loaded. Try again shortly.</p>
          ) : (
            <Skeleton className="h-24 w-full" />
          )
        ) : settled ? (
          <p className="text-sm text-muted-foreground">This step has been decided, or the run has moved on.</p>
        ) : (
          <>
            <ApprovalDecision
              organizationId={item.organizationId}
              run={run}
              nodeId={item.nodeId}
              viewerEmail={account?.email}
            />
            <ChecklistDone organizationId={item.organizationId} run={run} nodeId={item.nodeId} viewerEmail={account?.email} />
          </>
        )}
      </div>
    </DetailShell>
  );
}

/** What sits in the update's header: beside the title, and at the right edge. */
interface DetailActions {
  readonly title: React.ReactNode;
  readonly view: React.ReactNode;
}

function DetailShell({
  markerClassName,
  kindLabel,
  title,
  footer,
  children,
  properties,
  showProperties,
  contextPanel,
  detailActions,
}: {
  readonly markerClassName: string;
  readonly kindLabel: string;
  /** The update's own title, beside the kind, as Linear shows "AMO-8  Title". */
  readonly title: string;
  /**
   * What this update can be acted on with, as buttons for the closing footer.
   * The header has no action slot at all any more: it carries view chrome
   * (read, copy, properties, close), and decisions live in the footer.
   */
  readonly footer?: React.ReactNode;
  readonly children: React.ReactNode;
  readonly properties: React.ReactNode;
  readonly showProperties: boolean;
  readonly contextPanel: React.ReactNode;
  readonly detailActions: DetailActions;
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="flex min-h-10 shrink-0 flex-wrap items-center gap-2 border-b border-border/60 bg-background px-4 py-1.5">
        <span className="flex min-w-0 items-center gap-2 text-[13px] font-medium text-foreground">
          <span className="shrink-0 text-muted-foreground">{kindLabel}</span>
          <span className="truncate">{title}</span>
        </span>
        {/* Beside the title, as Linear keeps them: favourite, then more. */}
        <div className="flex shrink-0 items-center gap-0.5">{detailActions.title}</div>
        <div className="ml-auto flex min-w-0 flex-wrap items-center justify-end gap-1">
          {detailActions.view}
        </div>
      </header>
      <div className="flex min-h-0 flex-1">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <article className="min-w-0 flex-1 overflow-y-auto px-5 py-6 md:px-7 md:py-8">
            <div className="mx-auto max-w-xl">
              {children}
              {footer}
              {showProperties && (
                <section className="mt-6 border-t border-border pt-4 xl:hidden">
                  <h3 className="text-[13px] font-medium text-muted-foreground">Properties</h3>
                  <dl className="mt-4 space-y-3 text-[13px]">{properties}</dl>
                  {contextPanel}
                </section>
              )}
            </div>
          </article>
        </div>
        <aside
          aria-label="Update properties"
          aria-hidden={!showProperties}
          inert={!showProperties}
          className={cn(
            "hidden min-w-0 shrink-0 overflow-hidden transition-[width] duration-(--sidebar-motion-duration) ease-(--sidebar-motion-ease) motion-reduce:transition-none xl:block",
            showProperties ? "w-64" : "w-0",
          )}
        >
          <div
            className={cn(
              "h-full w-64 overflow-y-auto px-5 py-6 md:py-8 transition-[translate,opacity] duration-(--sidebar-motion-duration) ease-(--sidebar-motion-ease) motion-reduce:transition-none",
              showProperties ? "translate-x-0 opacity-100" : "translate-x-4 opacity-0",
            )}
          >
            <h3 className="text-[13px] font-medium text-muted-foreground">Properties</h3>
            <dl className="mt-4 space-y-3.5 text-[13px]">{properties}</dl>
            {contextPanel}
          </div>
        </aside>
      </div>
    </div>
  );
}

function InvitationDetail({
  item,
  showProperties,
  contextPanel,
  detailActions,
}: {
  readonly item: InboxInvitationItem;
  readonly showProperties: boolean;
  readonly contextPanel: React.ReactNode;
  readonly detailActions: DetailActions;
}) {
  const { capabilities, answer } = useInbox();
  const { refresh: refreshOrgs } = useOrgs();
  const navigate = useNavigate();
  const [busy, setBusy] = useState<"accept" | "decline" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const canAnswer = capabilities.acceptInvitation && !isSampleInboxItem(item);

  const respond = (action: "accept" | "decline") => {
    setBusy(action);
    setError(null);
    void answer(item.id, action).then((outcome) => {
      setBusy(null);
      if (!outcome.ok) {
        setError(outcome.message);
        return;
      }
      if (outcome.action === "accept") {
        // The organization switcher learns about the new membership, then
        // the person lands in the organization they just joined.
        refreshOrgs();
        void navigate({
          to: "/$org",
          params: { org: orgUrlSegment(outcome.joined.organizationId) },
        });
      }
    });
  };

  return (
    <DetailShell
      showProperties={showProperties}
      contextPanel={contextPanel}
      detailActions={detailActions}
      markerClassName="bg-success-foreground"
      kindLabel="Invitation"
      title={item.organizationName}
      footer={
        canAnswer ? (
          <InboxDetailFooter>
            {/* Primary first in the DOM, right-most on screen. */}
            <Button size="sm" disabled={busy !== null} onClick={() => respond("accept")}>
              {busy === "accept" ? "Joining…" : "Accept invitation"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={busy !== null}
              onClick={() => respond("decline")}
            >
              {busy === "decline" ? "Declining…" : "Decline"}
            </Button>
          </InboxDetailFooter>
        ) : undefined
      }
      properties={
        <>
          <PropertyRow label="Role" icon={<UserRoundIcon />}>
            <Badge size="sm" variant="secondary" className="capitalize">
              {item.role}
            </Badge>
          </PropertyRow>
          <PropertyRow label="Organization" icon={<Building2Icon />}>{item.organizationName}</PropertyRow>
          {item.invitedBy ? <PropertyRow label="Invited by" icon={<UserPlusIcon />}>{item.invitedBy}</PropertyRow> : null}
          <PropertyRow label="Received" icon={<Clock3Icon />}>{relativeLabel(item.createdAt)} ago</PropertyRow>
          <PropertyRow label="Expires" icon={<Clock3Icon />}>in {expiresIn(item.expiresAt)}</PropertyRow>
        </>
      }
    >
      <h2 className="text-2xl font-semibold leading-[1.3] tracking-tight text-balance">
        Invitation to join {item.organizationName}
      </h2>
      <p className="mt-5 text-[15px] leading-7 text-foreground/90">
        {item.invitedBy ? (
          <>
            <strong className="font-medium text-foreground">{item.invitedBy}</strong> invited you
          </>
        ) : (
          "You've been invited"
        )}{" "}
        to join <strong className="font-medium text-foreground">{item.organizationName}</strong> as{" "}
        {item.role === "admin" ? "an administrator" : "a member"}, and the invitation expires in{" "}
        {expiresIn(item.expiresAt)}. Accepting adds the organization to your workspace: its chats,
        policies, and regulatory monitoring become available under your account.
      </p>
      {/* Why there are no decisions, told accurately: a design fixture answers
          nothing by construction, and a server without invitation forwarding is
          a server problem — never the reader's account, which is signed in with
          the invited address or this item would not have been delivered. */}
      {canAnswer ? null : isSampleInboxItem(item) ? (
        <p className="mt-3 text-xs leading-5 text-muted-foreground">
          This is a sample invitation, so it offers no membership actions.
        </p>
      ) : (
        <p className="mt-3 text-xs leading-5 text-muted-foreground">
          Answering invitations isn't configured on this server. Use the link in your invitation
          email to join {item.organizationName}.
        </p>
      )}
      {error ? (
        <p role="alert" className="mt-3 text-sm text-destructive">
          {error}
        </p>
      ) : null}
    </DetailShell>
  );
}

function PolicyReviewDetail({
  item,
  showProperties,
  contextPanel,
  detailActions,
}: {
  readonly item: InboxPolicyReviewItem;
  readonly showProperties: boolean;
  readonly contextPanel: React.ReactNode;
  readonly detailActions: DetailActions;
}) {
  const severity = SEVERITY_BADGE[item.severity];
  return (
    <DetailShell
      showProperties={showProperties}
      contextPanel={contextPanel}
      detailActions={detailActions}
      markerClassName={severity.dotClass}
      kindLabel="Monitor"
      title={item.regulationTitle}
      properties={
        <>
          <PropertyRow label="Severity" icon={<InboxStatusGlyph status={{ kind: "severity", severity: item.severity }} />}>
            {severity.label}
          </PropertyRow>
          <PropertyRow label="Jurisdiction" icon={<GlobeIcon />}>{item.jurisdiction}</PropertyRow>
          <PropertyRow label="Policies" icon={<FileTextIcon />}>{item.affectedPolicies.length}</PropertyRow>
          <PropertyRow label="Detected" icon={<Clock3Icon />}>{relativeLabel(item.createdAt)} ago</PropertyRow>
        </>
      }
    >
      <h2 className="max-w-[36ch] text-2xl font-semibold leading-[1.3] tracking-tight text-balance">
        {item.regulationTitle}
      </h2>
      <p className="mt-5 text-[15px] leading-7 text-foreground/90">{item.summary}</p>
      <section className="mt-9">
        <h3 className="flex items-center gap-2 text-[15px] font-semibold">
          Affected policies
          <span className="text-[13px] font-normal tabular-nums text-muted-foreground">
            {item.affectedPolicies.length}
          </span>
        </h3>
        <InboxPolicyLinks names={item.affectedPolicies} />
      </section>
    </DetailShell>
  );
}

/**
 * A property as Linear lists them: an icon that says which property, then
 * the value — "◉ Done", "▮▮▮ High". The label stays for assistive tech and as
 * a tooltip; on screen the icon carries it.
 */
function PropertyRow({
  label,
  icon,
  children,
}: {
  readonly label: string;
  readonly icon?: React.ReactNode;
  readonly children: React.ReactNode;
}) {
  return (
    <div className="flex min-w-0 items-start gap-2.5" title={label}>
      <dt className="flex size-5 shrink-0 items-center justify-center text-muted-foreground [&_svg]:size-3.5">
        <span className="sr-only">{label}</span>
        {icon ?? <span aria-hidden className="size-1.5 rounded-full bg-muted-foreground/50" />}
      </dt>
      <dd className="min-w-0 break-words text-[13px] leading-5 text-foreground">{children}</dd>
    </div>
  );
}

function expiresIn(isoDate: string): string {
  const expires = new Date(isoDate).getTime();
  if (Number.isNaN(expires)) return "—";
  const days = Math.max(0, Math.ceil((expires - Date.now()) / (24 * 60 * 60 * 1000)));
  return days === 0 ? "less than a day" : days === 1 ? "1 day" : `${days} days`;
}

function AgentNotificationDetail({
  item,
  showProperties,
  contextPanel,
  detailActions,
}: {
  readonly item: InboxAgentNotification;
  readonly showProperties: boolean;
  readonly contextPanel: React.ReactNode;
  readonly detailActions: DetailActions;
}) {
  // Resolved once here and handed to the body, so the footer's single action
  // and the per-record rows agree about what still exists.
  const available = useNotificationLinkedRecords(item);
  const destination = notificationPrimaryDestination(item, available);
  // Monitoring never outranks a linked record, but an update that carries both
  // must still be able to reach the Monitor page.
  const secondary = notificationSecondaryDestinations(item, available);
  return (
    <DetailShell
      markerClassName={SEVERITY_BADGE[item.severity].dotClass}
      kindLabel={notificationSourceLabel(item.source)}
      title={item.title}
      detailActions={detailActions}
      footer={
        <InboxDetailFooter>
          {destination ? (
            <NotificationDestinationLink item={item} destination={destination} />
          ) : null}
          {secondary.map((each) => (
            <NotificationDestinationLink
              key={each.kind}
              item={item}
              destination={each}
              variant="outline"
            />
          ))}
          <NotificationRunLink item={item} variant={destination ? "outline" : "default"} />
        </InboxDetailFooter>
      }
      showProperties={showProperties}
      contextPanel={contextPanel}
      properties={
        <>
          <PropertyRow label="From" icon={<SparklesIcon />}>
            <span className="inline-flex items-center gap-1.5">
              {item.source === "inspector" ? <InspectorIcon className="size-3.5" /> : null}
              {notificationSourceLabel(item.source)}
            </span>
          </PropertyRow>
          <PropertyRow label="Organization" icon={<Building2Icon />}>{item.organizationName}</PropertyRow>
          <PropertyRow label="Priority" icon={<InboxStatusGlyph status={{ kind: "severity", severity: item.severity }} />}>{SEVERITY_BADGE[item.severity].label}</PropertyRow>
          <PropertyRow label="Received" icon={<Clock3Icon />}>{relativeLabel(item.createdAt)}</PropertyRow>
        </>
      }
    >
      <NotificationBody item={item} available={available} />
    </DetailShell>
  );
}
